/**
 * Slice 42 — composing a Meta ad in Wend, and publishing it to Meta **paused**.
 *
 * ## NOTHING IN THIS SLICE CAN SPEND MONEY
 *
 * That is the design, not a limitation. Every campaign, ad set and ad this
 * slice creates at Meta is created with `status: PAUSED`, from one constant
 * ({@link META_CREATE_STATUS}) that the write orchestration uses for all three
 * and that a suite asserts every one of those creates carried. Slice 43 owns the one
 * path that can make anything active. So a bug anywhere in here — a wrong
 * budget, a duplicate campaign from a retried publish, a draft published twice
 * — produces paused objects in Ads Manager, which cost nothing, rather than
 * spend.
 *
 * ## DRAFT FIRST, AND A HUMAN SHIPS WHAT THEY REVIEWED
 *
 * A draft is composed and stored in Wend. Publishing it is a separate, explicit
 * act, and it must name the {@link MetaAdDraftRecord.version} the person
 * reviewed: an edit that landed after they looked is a conflict, not a
 * surprise at Meta. "Human in the loop" with no version check is a human
 * approving whatever the draft happens to say by the time the request arrives.
 *
 * ## BANKS ADVERTISE FINANCIAL PRODUCTS, AND META TREATS THEM DIFFERENTLY
 *
 * Wend's customers are cooperative banks, and an ad for a loan, a deposit or
 * an insurance cover is an ad for *financial products and services* — the
 * special ad category Meta put in the place of credit on 14 January 2025
 * (`FINANCIAL_PRODUCTS_SERVICES`; Meta no longer accepts `CREDIT`, and slices
 * 42 to 46 offered it until slice 47's review found that). Meta asks ads about
 * financial products and services, housing and employment to declare a
 * special ad category — a requirement in a growing list of countries, which
 * this repository cannot confirm for any particular one — and a campaign that
 * declares one may not target by age or gender. So:
 *
 *   - the category is a **required, explicit choice with no default**. It is a
 *     declaration the advertiser makes to Meta, and Wend guessing it — in either
 *     direction — would be Wend making a legal statement on a bank's behalf;
 *   - a draft that declares a restricted category and narrows age or gender is
 *     **refused here**, with the reason, rather than accepted and rejected by
 *     Meta later on a campaign somebody thought was ready.
 *
 * ## MONEY GOES IN AS A HUMAN TYPES IT AND IS STORED AS META COUNTS IT
 *
 * A request carries a budget as a decimal string in major units — `"2000"` or
 * `"2000.50"`, what somebody types. It is converted once, exactly, to basic
 * units in the connected account's currency by the same text parser the read
 * uses for insights spend (`metaSpendToMinor`), and stored that way with the
 * currency beside it. The request names the currency the amount was typed
 * against, and saving refuses one that is not the account's now; publishing
 * refuses if the account's currency has changed since the save. A budget of
 * 200000 means ₹2,000 or $2,000 depending on that one field, and sending it to
 * an account in the other currency is a mistake the size of the exchange rate
 * — about eighty-fold between rupees and dollars — that looks like a number.
 *
 * ## AN AD OPENS A WEBSITE, OR A WHATSAPP CHAT (SLICE 47)
 *
 * A draft's ads open a website — the three formats slice 42 built, each with
 * its link and its button — or a WhatsApp chat with the business: Meta's
 * "ads that click to WhatsApp", as `chat_image` and `chat_video`. A chat ad
 * has no link and no button to choose: Meta's recipe fixes both (the
 * `WHATSAPP_MESSAGE` button, and a chat address that is the same for every
 * such ad), so neither is a field a person could get wrong. Where an ad set's
 * ads lead is one setting at Meta (`destination_type`), so every ad in a draft
 * opens the same place — a draft's destination is its ads'
 * ({@link metaDraftDestination}), and `draftRules` refuses a draft whose ads
 * disagree.
 *
 * A chat ad is the only kind a draft puts on **WhatsApp Status**: Meta's
 * placement reference documents the `whatsapp` placement, at its one position
 * `status`, for an ad set set up to click to WhatsApp, and only with the
 * Instagram Stories placement beside it — so a draft that names Status names
 * Instagram too, and opens a chat. Meta's own pages say Status can send
 * people to a website as well; the reference does not, and a draft follows
 * the reference until a real account says otherwise. Of the objectives here
 * only traffic can open a chat (Meta's click-to-WhatsApp page allows
 * engagement, leads, sales and traffic), and a chat ad set optimises for
 * conversations ({@link metaAdSetDelivery}). A chat ad is an image or a video:
 * Meta's page allows carousels too, and Wend has not built one.
 *
 * ## WHAT IS DELIBERATELY NOT HERE
 *
 *   - **Lead campaigns.** A website lead needs a Meta instant form or a pixel,
 *     and drafting one without either produces a campaign that cannot run; a
 *     lead that opens a WhatsApp chat needs neither, and is not built either.
 *     Awareness and traffic only.
 *   - **Custom audiences from contact lists.** Uploading hashed contacts of real
 *     banks' customers to Meta is a consent decision under India's data
 *     protection law before it is a feature. Not built until that is decided.
 *   - **City and radius targeting.** Both need Meta's location search, a read
 *     the stub cannot answer honestly. Countries only.
 *   - **A text-only format.** Every format here carries text — the primary
 *     text, headline and description are the "text" of the ad — and every one
 *     also carries media. Wend offers no format without it.
 */
import { z } from 'zod';

import { isoDateTimeSchema, uuidSchema } from '../common.js';
import { metaCurrencyOffset, metaSpendToMinor } from './meta-ads-read.js';
import { DRAFTABLE_AD_SURFACES, surfaceSchema } from './surface.js';

/* -------------------------------------------------------------------------- */
/* The one constant that keeps this slice from spending                        */
/* -------------------------------------------------------------------------- */

/**
 * The status every object this slice creates at Meta is created in.
 *
 * Typed as the literal, so a caller cannot pass anything else through a
 * variable. The write orchestration names this constant for campaigns, ad sets
 * and ads alike; `verify-meta-ads-write` records every create request and
 * asserts each of those three kinds carried it — videos and creatives have no
 * status to carry — and that no request in the whole publish carried `ACTIVE`.
 */
export const META_CREATE_STATUS = 'PAUSED' as const;

/* -------------------------------------------------------------------------- */
/* Vocabulary                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * The objectives a draft may have. See the header for why leads waits.
 *
 * Each, with where the draft's ads lead, implies its ad set's optimisation
 * goal and billing event — see {@link metaAdSetDelivery} — so a person never
 * picks an incompatible pair that Meta would refuse.
 */
export const META_AD_OBJECTIVE_VALUES = ['OUTCOME_AWARENESS', 'OUTCOME_TRAFFIC'] as const;
export const metaAdObjectiveSchema = z.enum(META_AD_OBJECTIVE_VALUES);
export type MetaAdObjective = z.infer<typeof metaAdObjectiveSchema>;

/** Objective → what the ad set optimises for and pays for, for ads that open a website. Derived, never chosen. */
export const META_OBJECTIVE_DELIVERY: Readonly<
  Record<MetaAdObjective, { readonly optimizationGoal: string; readonly billingEvent: string }>
> = Object.freeze({
  OUTCOME_AWARENESS: { optimizationGoal: 'REACH', billingEvent: 'IMPRESSIONS' },
  OUTCOME_TRAFFIC: { optimizationGoal: 'LINK_CLICKS', billingEvent: 'IMPRESSIONS' },
});

/**
 * Slice 47. An ad set of chat ads: conversations started, paid by impressions.
 * Meta's click-to-WhatsApp page lists `CONVERSATIONS` among a traffic
 * objective's goals, and traffic is the only objective a chat draft may have.
 */
export const META_CHAT_DELIVERY: { readonly optimizationGoal: string; readonly billingEvent: string } = Object.freeze({
  optimizationGoal: 'CONVERSATIONS',
  billingEvent: 'IMPRESSIONS',
});

/**
 * Special ad categories, as Meta accepts them today. `NONE` is a choice like
 * the others — see the header: the field has no default, because it is a
 * declaration.
 */
export const META_SPECIAL_AD_CATEGORY_VALUES = [
  'NONE',
  'FINANCIAL_PRODUCTS_SERVICES',
  'HOUSING',
  'EMPLOYMENT',
  'ISSUES_ELECTIONS_POLITICS',
] as const;
/**
 * A draft stored before slice 47 may say `CREDIT`, which Meta no longer
 * accepts and replaced with financial products and services — so it is read
 * as that, the declaration Meta itself put in its place, and a draft is never
 * refused for what Wend once offered.
 */
export const metaSpecialAdCategorySchema = z.preprocess(
  (value) => (value === 'CREDIT' ? 'FINANCIAL_PRODUCTS_SERVICES' : value),
  z.enum(META_SPECIAL_AD_CATEGORY_VALUES),
);
export type MetaSpecialAdCategory = (typeof META_SPECIAL_AD_CATEGORY_VALUES)[number];

/**
 * The categories under which Meta refuses age and gender targeting.
 *
 * Exported so the composer can say so *before* somebody fills in an age range,
 * rather than only after the save is refused.
 */
export const META_RESTRICTED_TARGETING_CATEGORIES: readonly MetaSpecialAdCategory[] = [
  'FINANCIAL_PRODUCTS_SERVICES',
  'HOUSING',
  'EMPLOYMENT',
];

/** How a refusal names an ad of each category — a sentence's subject, not the enum's words. */
const CATEGORY_AD: Readonly<Record<MetaSpecialAdCategory, string>> = {
  NONE: 'This ad',
  FINANCIAL_PRODUCTS_SERVICES: 'An ad for financial products and services',
  HOUSING: 'A housing ad',
  EMPLOYMENT: 'An employment ad',
  ISSUES_ELECTIONS_POLITICS: 'An ad about social issues, elections or politics',
};

/** Call-to-action buttons a bank's ad plausibly wants. Meta's own type names. */
export const META_CTA_VALUES = [
  'LEARN_MORE',
  'APPLY_NOW',
  'SIGN_UP',
  'CONTACT_US',
  'GET_QUOTE',
  'SUBSCRIBE',
] as const;
export const metaCtaSchema = z.enum(META_CTA_VALUES);
export type MetaCta = z.infer<typeof metaCtaSchema>;

/** The youngest and oldest ages Meta targets. 65 means "65 and over". */
export const META_AGE_MIN = 18;
export const META_AGE_MAX = 65;

/* -------------------------------------------------------------------------- */
/* Pieces                                                                      */
/* -------------------------------------------------------------------------- */

const metaIdSchema = z.string().regex(/^\d{1,24}$/, 'Meta ids are numeric. Paste the id, not the name or the URL.');

/**
 * A link or a media URL. `https` only: Meta fetches images and videos by URL,
 * and a link ad pointing at plain http sends a bank's customers somewhere a
 * browser will mark as not secure.
 */
const httpsUrlSchema = z
  .string()
  .trim()
  .url('Paste a full address, starting with https://')
  .max(2000)
  .refine((value) => value.startsWith('https://'), 'The address must start with https://');

/*
 * Sanity bounds on text, NOT Meta's limits. Meta's own limits vary by field,
 * placement and API version, and Meta refuses what it refuses at publish —
 * with its own reason on the draft, when the reason is fit to show (see
 * `publishFailure`). These only stop a paste of a whole document.
 */
const primaryTextSchema = z.string().trim().min(1, 'Write the text of the ad.').max(2000);
const headlineSchema = z.string().trim().min(1, 'Write a headline.').max(255);
const descriptionSchema = z.string().trim().max(255).nullable();

export const metaAdTargetingSchema = z
  .object({
    /** ISO 3166 country codes. */
    countries: z
      .array(z.string().regex(/^[A-Z]{2}$/, 'Use two-letter country codes, such as IN.'))
      .min(1, 'Choose at least one country.')
      .max(25),
    ageMin: z.number().int().min(META_AGE_MIN).max(META_AGE_MAX),
    ageMax: z.number().int().min(META_AGE_MIN).max(META_AGE_MAX),
    gender: z.enum(['all', 'male', 'female']),
    /**
     * `null` is Advantage+ placements — Meta decides, everywhere. Otherwise at
     * least one of Facebook, Instagram and — since slice 47 — `whatsapp`,
     * which is WhatsApp Status: a chat draft's, with Instagram beside it (see
     * the header). The same null-means-everywhere the read uses, so a draft
     * and a read of what it became agree.
     */
    placements: z
      .array(z.enum(['facebook', 'instagram', 'whatsapp']))
      .min(1, 'Choose at least one placement.')
      .max(3)
      .nullable(),
  })
  .strict()
  .refine((targeting) => targeting.ageMin <= targeting.ageMax, {
    message: 'The youngest age must not be above the oldest.',
    path: ['ageMax'],
  });
export type MetaAdTargeting = z.infer<typeof metaAdTargetingSchema>;

/** One carousel card. */
export const metaCarouselCardSchema = z
  .object({
    imageUrl: httpsUrlSchema,
    headline: headlineSchema,
    description: descriptionSchema,
    link: httpsUrlSchema,
  })
  .strict();
export type MetaCarouselCard = z.infer<typeof metaCarouselCardSchema>;

/**
 * The creative. Three formats that open a website, each carrying the ad's
 * text, its media, its link and its button; and since slice 47 two that open
 * a WhatsApp chat, the same image or video with neither — see the header.
 *
 * Media is by `https` URL, which Meta fetches: an image as a link ad's
 * `picture`, a video by uploading it from `file_url` at publish. Meta processes
 * video asynchronously, so a video ad can be created before its video is
 * ready — it is paused either way.
 */
export const metaAdCreativeSchema = z.discriminatedUnion('format', [
  z
    .object({
      format: z.literal('single_image'),
      imageUrl: httpsUrlSchema,
      primaryText: primaryTextSchema,
      headline: headlineSchema,
      description: descriptionSchema,
      cta: metaCtaSchema,
      link: httpsUrlSchema,
    })
    .strict(),
  z
    .object({
      format: z.literal('carousel'),
      primaryText: primaryTextSchema,
      cta: metaCtaSchema,
      cards: z
        .array(metaCarouselCardSchema)
        .min(2, 'A carousel needs at least two cards.')
        .max(10, 'A carousel holds at most ten cards.'),
    })
    .strict(),
  z
    .object({
      format: z.literal('single_video'),
      videoUrl: httpsUrlSchema,
      /** Meta requires a still image for a video ad. */
      thumbnailUrl: httpsUrlSchema,
      primaryText: primaryTextSchema,
      headline: headlineSchema,
      description: descriptionSchema,
      cta: metaCtaSchema,
      link: httpsUrlSchema,
    })
    .strict(),
  /* Slice 47: an image that opens a WhatsApp chat. */
  z
    .object({
      format: z.literal('chat_image'),
      imageUrl: httpsUrlSchema,
      primaryText: primaryTextSchema,
      headline: headlineSchema,
      description: descriptionSchema,
    })
    .strict(),
  /* Slice 47: a video that opens a WhatsApp chat. */
  z
    .object({
      format: z.literal('chat_video'),
      videoUrl: httpsUrlSchema,
      thumbnailUrl: httpsUrlSchema,
      primaryText: primaryTextSchema,
      headline: headlineSchema,
      description: descriptionSchema,
    })
    .strict(),
]);
export type MetaAdCreative = z.infer<typeof metaAdCreativeSchema>;

/** Where an ad leads. */
export const META_AD_DESTINATION_VALUES = ['website', 'whatsapp'] as const;
export type MetaAdDestination = (typeof META_AD_DESTINATION_VALUES)[number];

/** Where one creative leads — a chat format's chat, every other format's link. */
export function metaCreativeDestination(creative: MetaAdCreative): MetaAdDestination {
  return creative.format === 'chat_image' || creative.format === 'chat_video' ? 'whatsapp' : 'website';
}

/**
 * Where a draft's ads lead — its first ad's, which is every ad's: a draft whose
 * ads disagree is refused (`draftRules`), because the ad set that holds them
 * has one destination at Meta.
 */
export function metaDraftDestination(draft: { readonly ads: readonly { readonly creative: MetaAdCreative }[] }): MetaAdDestination {
  const first = draft.ads[0];
  return first === undefined ? 'website' : metaCreativeDestination(first.creative);
}

/** The ad set's optimisation goal and billing event: the objective's, or a chat ad set's. */
export function metaAdSetDelivery(
  objective: MetaAdObjective,
  destination: MetaAdDestination,
): { readonly optimizationGoal: string; readonly billingEvent: string } {
  return destination === 'whatsapp' ? META_CHAT_DELIVERY : META_OBJECTIVE_DELIVERY[objective];
}

export const metaDraftAdSchema = z
  .object({
    name: z.string().trim().min(1, 'Name the ad.').max(200),
    creative: metaAdCreativeSchema,
  })
  .strict();
export type MetaDraftAd = z.infer<typeof metaDraftAdSchema>;

/** At most five ads in one draft. More is a second draft, not a longer form. */
export const META_DRAFT_MAX_ADS = 5;

/* -------------------------------------------------------------------------- */
/* The draft, as requested and as stored                                       */
/* -------------------------------------------------------------------------- */

const budgetKindSchema = z.enum(['daily', 'lifetime']);

/**
 * An amount as a person types it: major units, at most two decimals — the
 * currency then decides how many it may really have (see
 * {@link typedAmountToMinor}). Shared by a draft's budget and slice 43's
 * budget changes and spend caps, so all three accept the same text.
 */
export const metaTypedAmountSchema = z
  .string()
  .trim()
  .regex(/^\d{1,13}(\.\d{1,2})?$/, 'Write the amount as a number, e.g. 2000 or 2000.50.');

/**
 * Everything but the budget, which is the one field whose shape differs
 * between what a person sends and what is stored.
 */
const draftShape = {
  campaign: z
    .object({
      name: z.string().trim().min(1, 'Name the campaign.').max(200),
      objective: metaAdObjectiveSchema,
      specialAdCategory: metaSpecialAdCategorySchema,
    })
    .strict(),
  /**
   * The Facebook Page the ads run as. Meta refuses a link ad without one; a
   * chat ad's conversations reach the WhatsApp number linked to it.
   */
  pageId: metaIdSchema,
  /**
   * Optional: without it, Meta decides how the ad appears on Instagram. Sent
   * as `instagram_user_id` — Meta retired `instagram_actor_id` in v22.0.
   */
  instagramAccountId: metaIdSchema.nullable(),
  ads: z.array(metaDraftAdSchema).min(1, 'Add at least one ad.').max(META_DRAFT_MAX_ADS),
};

/** The ad set, without its budget. */
const adSetShape = {
  name: z.string().trim().min(1, 'Name the ad set.').max(200),
  /** `YYYY-MM-DD` in the ad account's zone; the last day the ad set runs. */
  endsOn: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable(),
  targeting: metaAdTargetingSchema,
};

/**
 * The rules that are about the draft as a whole rather than any one field.
 * Shared by the request and the stored shape, so a stored draft cannot hold
 * what a request could not.
 */
function draftRules(
  draft: {
    campaign: { specialAdCategory: MetaSpecialAdCategory; objective: MetaAdObjective };
    adSet: { budget: { kind: 'daily' | 'lifetime' }; endsOn: string | null; targeting: MetaAdTargeting };
    ads: readonly { creative: MetaAdCreative }[];
  },
  ctx: z.RefinementCtx,
): void {
  const restricted = META_RESTRICTED_TARGETING_CATEGORIES.includes(draft.campaign.specialAdCategory);
  const { targeting } = draft.adSet;
  const kind = CATEGORY_AD[draft.campaign.specialAdCategory];

  if (restricted && (targeting.ageMin !== META_AGE_MIN || targeting.ageMax !== META_AGE_MAX)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['adSet', 'targeting', 'ageMin'],
      message: `${kind} cannot be targeted by age. Meta requires the full range, ${String(META_AGE_MIN)} to ${String(META_AGE_MAX)}+.`,
    });
  }
  if (restricted && targeting.gender !== 'all') {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['adSet', 'targeting', 'gender'],
      message: `${kind} cannot be targeted by gender.`,
    });
  }
  if (draft.adSet.budget.kind === 'lifetime' && draft.adSet.endsOn === null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['adSet', 'endsOn'],
      message: 'A lifetime budget needs an end date — it is spread over the days until then.',
    });
  }

  /* Slice 47 — where the ads lead, and WhatsApp Status. See the header. */
  const destination = metaDraftDestination(draft);
  const strayAt = draft.ads.findIndex((ad) => metaCreativeDestination(ad.creative) !== destination);
  if (strayAt >= 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['ads', strayAt, 'creative', 'format'],
      message: 'Every ad in a draft opens the same place — a website, or a WhatsApp chat.',
    });
  }
  if (destination === 'whatsapp' && draft.campaign.objective !== 'OUTCOME_TRAFFIC') {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['campaign', 'objective'],
      message: 'An ad that opens a WhatsApp chat cannot be an awareness ad. Choose Traffic.',
    });
  }
  const placements = targeting.placements ?? [];
  if (placements.includes('whatsapp') && destination !== 'whatsapp') {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['adSet', 'targeting', 'placements'],
      message: 'A draft names WhatsApp Status only for ads that open a WhatsApp chat.',
    });
  } else if (placements.includes('whatsapp') && !placements.includes('instagram')) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['adSet', 'targeting', 'placements'],
      message: 'WhatsApp Status runs beside Instagram Stories. Choose Instagram too.',
    });
  }
}

/**
 * What a person sends. The budget amount is a decimal string in major units,
 * as typed — see the header.
 *
 * `currency` is the currency the amount was typed against: the one the
 * composer showed beside it. The API converts only in the currency the
 * connected account reports at the moment of saving, and refuses an amount
 * typed against any other — otherwise a composer opened before a USD account
 * was connected would save "2000.50" typed as rupees as $2,000.50.
 */
export const metaAdDraftRequestSchema = z
  .object({
    ...draftShape,
    adSet: z
      .object({
        ...adSetShape,
        budget: z
          .object({
            kind: budgetKindSchema,
            amount: metaTypedAmountSchema,
            currency: z.string().regex(/^[A-Z]{3}$/),
          })
          .strict(),
      })
      .strict(),
  })
  .strict()
  .superRefine(draftRules);
export type MetaAdDraftRequest = z.infer<typeof metaAdDraftRequestSchema>;

/** What is stored and returned. The budget is basic units; the currency is on the record. */
export const metaAdDraftSchema = z
  .object({
    ...draftShape,
    adSet: z
      .object({
        ...adSetShape,
        budget: z.object({ kind: budgetKindSchema, amount: z.number().int().positive() }).strict(),
      })
      .strict(),
  })
  .strict()
  .superRefine(draftRules);
export type MetaAdDraft = z.infer<typeof metaAdDraftSchema>;

/**
 * An amount a person typed, in major units, against the currency they were
 * shown → basic units in the account's currency. **The one place any typed
 * amount becomes money** — a draft's budget (slice 42), a budget change and a
 * spend cap (slice 43) all go through here, so all three refuse the same way.
 *
 * A refusal comes back with its reason — a currency this product will not
 * guess, an amount typed against a different currency from the account's,
 * more decimals than the currency has, or nothing at all — and never as a
 * rounded or guessed amount. `what` names the amount in the sentence ("The
 * budget", "The spend cap").
 */
export function typedAmountToMinor(
  amount: string,
  typedCurrency: string,
  accountCurrency: string,
  what: string,
): { readonly ok: true; readonly minor: number } | { readonly ok: false; readonly message: string } {
  const offset = metaCurrencyOffset(accountCurrency);
  if (offset === null) {
    return {
      ok: false,
      message: `Wend cannot take amounts in ${accountCurrency} yet — it does not know how Meta counts that currency, and will not guess.`,
    };
  }
  if (typedCurrency !== accountCurrency) {
    return {
      ok: false,
      message: `This amount was typed in ${typedCurrency}, and the ad account now reports in ${accountCurrency}. Enter it again in ${accountCurrency}.`,
    };
  }
  const minor = metaSpendToMinor(amount, accountCurrency);
  if (minor === null) {
    /* The request's own pattern allows two decimals, so an amount it accepted
       fails here only by carrying more decimals than this currency has. */
    const digits = Math.round(Math.log10(offset));
    return {
      ok: false,
      message:
        digits === 0
          ? `${accountCurrency} has no smaller unit. Write the amount as a whole number.`
          : `${accountCurrency} amounts have at most ${String(digits)} decimal places.`,
    };
  }
  if (minor <= 0) return { ok: false, message: `${what} must be more than zero.` };
  return { ok: true, minor };
}

/**
 * A request → the stored draft, in the connected account's currency.
 *
 * The budget goes through {@link typedAmountToMinor}; every refusal from it is
 * about the amount, so the caller reports it on `adSet.budget.amount`. The
 * composer runs this same function before it sends, so a person sees these on
 * the field rather than after a round trip.
 */
export function draftFromRequest(
  request: MetaAdDraftRequest,
  currency: string,
): { readonly ok: true; readonly draft: MetaAdDraft } | { readonly ok: false; readonly message: string } {
  const amount = typedAmountToMinor(request.adSet.budget.amount, request.adSet.budget.currency, currency, 'The budget');
  if (!amount.ok) return amount;
  const parsed = metaAdDraftSchema.safeParse({
    ...request,
    adSet: { ...request.adSet, budget: { kind: request.adSet.budget.kind, amount: amount.minor } },
  });
  return parsed.success
    ? { ok: true, draft: parsed.data }
    : { ok: false, message: parsed.error.issues[0]?.message ?? 'The draft is not valid.' };
}

/**
 * Whether `endsOn` is today or later in the account's zone.
 *
 * Asked by the service with the clock and the account's zone, not by the
 * schema, which has neither — and asked in the account's zone for the reason
 * every Meta window is: the end of a day in Kolkata is not the end of the same
 * date in UTC.
 */
export function endsOnIsFuture(endsOn: string, now: Date, timezone: string): boolean {
  let today: string;
  try {
    today = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(now);
  } catch {
    return false;
  }
  return endsOn >= today;
}

/* -------------------------------------------------------------------------- */
/* The record                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Where a draft is in its life.
 *
 *   draft       — being composed; editable.
 *   publishing  — a publish is running. Nothing else may touch it.
 *   published   — every object was created at Meta, paused. What Wend did,
 *                 not what Meta shows now: since slice 43 the draft page
 *                 reads Meta, and `delivery` records what Wend has done to
 *                 the campaign since.
 *   failed      — a publish stopped. What Meta confirmed it created is
 *                 recorded in `published`, so the next publish resumes rather
 *                 than creating it twice. The one exception is an object Meta
 *                 never confirmed — a create cut off or answered with a 5xx —
 *                 and the reason then says to check Ads Manager first.
 *                 **Correctable, and only in the parts nothing exists for**
 *                 (slice 52): every part with an id on file is frozen by
 *                 `metaDraftCorrection`, everything else is edited as usual,
 *                 and the next publish resumes into the ids already recorded.
 *                 A draft whose budget Meta refused as under its minimum is
 *                 the case this was built for. The budget of a *published*
 *                 draft is still slice 43's change, made at Meta, not an edit.
 */
export const META_DRAFT_STATUS_VALUES = ['draft', 'publishing', 'published', 'failed'] as const;

/**
 * How long a `publishing` row may go untouched before it is treated as a
 * publish that died, and may be claimed again.
 *
 * A live publish touches its row with every id it records, and each create is
 * one Graph request bounded to seconds, so five quiet minutes almost always
 * means dead. "Almost" is why migration 0030 exists: a publish that was only
 * slow is not trusted to have stopped, and its claim token is what shuts out
 * whatever it writes afterwards.
 *
 * Shared so the screen offers "Publish again" on the same threshold as the
 * API — measured on the browser's clock there and on the database's here, so
 * a browser whose clock runs fast offers it early and the API answers that the
 * publish is still running.
 */
export const META_PUBLISH_LEASE_MINUTES = 5;
export const metaDraftStatusSchema = z.enum(META_DRAFT_STATUS_VALUES);
export type MetaDraftStatus = z.infer<typeof metaDraftStatusSchema>;

/**
 * The Meta ids a publish has created so far — recorded as each one exists.
 *
 * Every field is filled in the moment Meta returns the id, not at the end of
 * the publish, so a publish that fails at the third ad resumes at the third
 * ad. Each array is indexed by the ad's position in the draft.
 *
 * ## THE ACCOUNT IS PART OF THE RECORD
 *
 * `adAccountId` is the account the campaign was created in, written in the same
 * record as `campaignId`. Everything after the campaign has to be created in
 * that account — an ad set names its campaign, an ad names its ad set. A
 * resume into an account connected since would be refused by Meta at the ad
 * set if it stopped before one; if it stopped after, it would first make the
 * video and the creative in the wrong account — those are made per account —
 * and be refused only at the ad. A resume therefore happens in the recorded
 * account or not at all, and a campaign id without its account does not parse.
 */
export const metaPublishedIdsSchema = z
  .object({
    adAccountId: z
      .string()
      .regex(/^\d{1,24}$/)
      .nullable(),
    campaignId: z.string().nullable(),
    adSetId: z.string().nullable(),
    /** Per ad: the uploaded video id, when the ad is a video. */
    videoIds: z.array(z.string().nullable()),
    creativeIds: z.array(z.string().nullable()),
    adIds: z.array(z.string().nullable()),
  })
  .strict()
  .refine((ids) => ids.campaignId === null || ids.adAccountId !== null, {
    message: 'A campaign is recorded without the ad account it was created in.',
    path: ['adAccountId'],
  });
export type MetaPublishedIds = z.infer<typeof metaPublishedIdsSchema>;

/** Nothing created yet, in no account yet, for a draft of `adCount` ads. */
export function metaPublishedNothing(adCount: number): MetaPublishedIds {
  const none = Array.from({ length: adCount }, () => null);
  return {
    adAccountId: null,
    campaignId: null,
    adSetId: null,
    videoIds: [...none],
    creativeIds: [...none],
    adIds: [...none],
  };
}

export const metaAdDraftRecordSchema = z
  .object({
    id: uuidSchema,
    /**
     * Incremented on every edit, and on every budget or spend cap change Wend
     * makes at Meta (slice 43). Publish and Start name the one the person
     * reviewed.
     */
    version: z.number().int().positive(),
    status: metaDraftStatusSchema,
    /** The account currency the budget was converted in. */
    currency: z.string().length(3),
    draft: metaAdDraftSchema,
    published: metaPublishedIdsSchema,
    /** Why the last publish stopped, in repair terms. Never a token. */
    lastError: z.string().nullable(),
    /**
     * Slice 43. What Wend last set the campaign's delivery to — `paused` from
     * publish until somebody starts it. What Wend DID, like "published
     * paused"; what Meta shows now is the delivery read. `unconfirmed` when
     * Meta never said whether Wend's last start or pause took: the campaign
     * may be running, and nothing here claims otherwise.
     */
    delivery: z.enum(['paused', 'active', 'unconfirmed']),
    /** Slice 43. The campaign spend cap Wend last set, basic units; `null` is none. */
    spendCap: z.number().int().positive().nullable(),
    /**
     * Slice 43. When Wend's first start was confirmed, or `null`. The first
     * start switches on the ads and the ad set as well as the campaign; every
     * later one flips the campaign alone (0032 says why).
     */
    startedAt: isoDateTimeSchema.nullable(),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
    publishedAt: isoDateTimeSchema.nullable(),
  })
  .strict();
export type MetaAdDraftRecord = z.infer<typeof metaAdDraftRecordSchema>;

/* -------------------------------------------------------------------------- */
/* The list — paged and searched on the server (slice 52, gap 4)                */
/* -------------------------------------------------------------------------- */

/**
 * `GET /meta-ads/drafts` — a page of drafts, newest change first.
 *
 * ## WHY `truncated` IS GONE
 *
 * Slice 42 listed the newest hundred and said nothing; slice 44 gave that a
 * "Partial list" chip, which reported the problem rather than fixing it: the
 * panel then filtered those hundred rows in the browser, so "No match" could
 * mean "not among the newest hundred", and a search past them found nothing
 * while a matching draft sat in the database. A list is now either continued
 * by "Show more" or complete, and both the search and the surface filter run
 * in SQL over the whole of the tenant's drafts — so "No match" means the whole
 * set, and the chip has nothing left to say.
 *
 * ## WHY KEYSET AND NOT AN OFFSET
 *
 * `(updated_at DESC, id DESC)`, carried in {@link MetaAdDraftCursor}. An
 * OFFSET re-walks the rows it skips and, worse, shifts under a list whose
 * order changes with every edit: a draft touched between two pages would be
 * listed twice or not at all. The id is in the key because two drafts written
 * by one seeding script share `updated_at` to the millisecond, and a cursor on
 * the timestamp alone silently drops or repeats one at every page boundary.
 * Migration 0038 indexes exactly this pair.
 *
 * ## WHAT IS DELIBERATELY NOT HERE
 *
 * No total count: a count query per keystroke, to print a number nobody acts
 * on. Each list counts what it has loaded, as slice 44 settled. And no
 * trigram index — the name search scans within the tenant, which for a few
 * thousand drafts is honest; if it ever is not, that is its own slice.
 */
export const META_AD_DRAFT_PAGE_DEFAULT = 25;
export const META_AD_DRAFT_PAGE_MAX = 100;
/** The longest search text the list accepts. Refused on the field before a request is made. */
export const META_AD_DRAFT_SEARCH_MAX = 80;

/**
 * Where the next page starts: the `(updated_at, id)` of the last row of this
 * one. Travels base64url'd, and is parsed by this schema before anything looks
 * at it — a cursor is a filter, not an identity, and never anything but bound
 * values in a query.
 */
export const metaAdDraftCursorSchema = z
  .object({ updatedAt: isoDateTimeSchema, id: uuidSchema })
  .strict();
export type MetaAdDraftCursor = z.infer<typeof metaAdDraftCursorSchema>;

/*
 * base64url, written out rather than borrowed.
 *
 * This package compiles with `lib: ES2022` and no ambient types at all, which
 * is deliberate — it is the one package both the API and two browsers import,
 * and it must not be able to reach for a platform API that exists in only one
 * of them. `Buffer` is Node's and `btoa` is the browser's, so neither is here.
 * The payload is a cursor: an ISO instant, a uuid and JSON punctuation — ASCII
 * throughout, which is why a byte-per-character encoder is correct for it and
 * would not be for arbitrary text.
 */
const B64URL = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';

function toBase64Url(text: string): string {
  let out = '';
  for (let at = 0; at < text.length; at += 3) {
    const a = text.charCodeAt(at);
    const b = at + 1 < text.length ? text.charCodeAt(at + 1) : -1;
    const c = at + 2 < text.length ? text.charCodeAt(at + 2) : -1;
    out += B64URL[a >> 2];
    out += B64URL[((a & 3) << 4) | (b < 0 ? 0 : b >> 4)];
    if (b < 0) break;
    out += B64URL[((b & 15) << 2) | (c < 0 ? 0 : c >> 6)];
    if (c < 0) break;
    out += B64URL[c & 63];
  }
  return out;
}

/** `null` for any character that is not base64url — a cursor nobody issued. */
function fromBase64Url(value: string): string | null {
  let bits = 0;
  let held = 0;
  let out = '';
  for (const character of value) {
    const index = B64URL.indexOf(character);
    if (index < 0) return null;
    held = (held << 6) | index;
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      out += String.fromCharCode((held >> bits) & 0xff);
    }
  }
  return out;
}

/** A cursor → the opaque string the list answers with. base64url: it travels in a query string. */
export function encodeMetaAdDraftCursor(cursor: MetaAdDraftCursor): string {
  return toBase64Url(JSON.stringify(cursor));
}

/**
 * An opaque cursor → the pair it carries, or `null` for anything that is not
 * one. `null` is a 400 the caller words; nothing here throws, because a cursor
 * arrives from a query string and a malformed one is an ordinary refusal.
 */
export function decodeMetaAdDraftCursor(value: string): MetaAdDraftCursor | null {
  const text = fromBase64Url(value);
  if (text === null) return null;
  try {
    const cursor = metaAdDraftCursorSchema.safeParse(JSON.parse(text));
    return cursor.success ? cursor.data : null;
  } catch {
    return null;
  }
}

/**
 * `GET /meta-ads/drafts?q=&surface=&cursor=&limit=`.
 *
 * `surface` is an ad surface a draft can name ({@link DRAFTABLE_AD_SURFACES}).
 * A message surface is not a draft's, so it is refused rather than answered
 * with an empty page that would read as "no ads on WhatsApp campaigns".
 */
export const metaAdDraftListQuerySchema = z
  .object({
    q: z.string().trim().max(META_AD_DRAFT_SEARCH_MAX).optional(),
    surface: surfaceSchema
      .refine((surface) => DRAFTABLE_AD_SURFACES.includes(surface), 'That is not an ad surface a draft can name.')
      .optional(),
    cursor: z.string().min(1).max(512).optional(),
    limit: z.coerce.number().int().min(1).max(META_AD_DRAFT_PAGE_MAX).default(META_AD_DRAFT_PAGE_DEFAULT),
  })
  .strict();
export type MetaAdDraftListQuery = z.infer<typeof metaAdDraftListQuerySchema>;

/** A page of drafts. `nextCursor` is `null` when this page is the end of the list. */
export const metaAdDraftListSchema = z
  .object({ drafts: z.array(metaAdDraftRecordSchema), nextCursor: z.string().nullable() })
  .strict();
export type MetaAdDraftList = z.infer<typeof metaAdDraftListSchema>;

/**
 * `POST /meta-ads/drafts/:id/publish`. The version is the one the person was
 * looking at when they pressed Publish.
 */
export const metaPublishRequestSchema = z.object({ version: z.number().int().positive() }).strict();
export type MetaPublishRequest = z.infer<typeof metaPublishRequestSchema>;

/**
 * `PUT /meta-ads/drafts/:id`. The version is the one the person was editing;
 * a save over somebody else's edit is a conflict, not a silent overwrite.
 */
export const metaAdDraftUpdateRequestSchema = z
  .object({ version: z.number().int().positive(), draft: metaAdDraftRequestSchema })
  .strict();
export type MetaAdDraftUpdateRequest = z.infer<typeof metaAdDraftUpdateRequestSchema>;
